import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef } from "react";
import jmespath from "jmespath";
import { Trans, useTranslation } from "react-i18next";
import { useFetch } from "../../../../hooks/api";
import { Container as Grid, Row, Col, Nav } from "react-bootstrap";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import moment from "moment";
import { DatesProvider } from "../../../../dates/DatesContext";
import { useDates } from "../../../../dates/DatesContext";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import { useMemo } from "react";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  parseFormattedCurrency,
  parseFormattedNumber,
  parseFormattedPercentage,
} from "../../../../utils/formatNumber";
import { metricColors, trellisPalette } from "../../../../components/custom/analytics/palettes";
import { getURLPrefix } from "../../../../utils/getUrlPrefix";
import ReactTable from "react-table-6";
import useColumnSelector from "../../../../hooks/useColumnSelector";
import { filterRanges, generateFilterMarkup, generateRanges } from "../../../../utils/rangeDropdownFilter";
import ReactTablePagination from "../../../../components/custom/growth/ProductsCVTablePagination";
import DownloadReport from "../../../../modules/perf_reports/DownloadReport";
import Loading from "../../../../components/core/blocks/Loading";
import DetailedMetric from "../../../../components/core/blocks/DetailedMetric";
import PaymentDiscovery from "../../../../views/billing/PaymentDiscovery";
import checkTierAccess from "../../../../utils/checkTierAccess";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../../../../utils/dashboardLinks";
import useCustomTableFilters from "../../../../hooks/useCustomTableFilters";
import useTableSettings from "../../../../hooks/useTableSettings";
import { RT6FilterWidget } from "../../../../modules/merchandising/components/tables/FilterWidget";
import { usePromotionReportFilters } from "../../../../modules/advertising/filters/promotionReportFilters";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { sortTableColumns } from "../../../../utils/tableUtils";
import { useAppSelector } from "../../../../redux/hooks";
import { keepPreviousData } from "@tanstack/react-query";
import { CategoryCell } from "../../../../components/custom/merchandise/cells/CategoryCell";
import NoImage from "../../../../assets/images/utility/no_image.png";

const URL_PREFIX = getURLPrefix();

const ProductCell = (props) => {
  const productId = props?.value?.id;
  const image = `${URL_PREFIX}/api/productimage/?product=${productId}`;
  return (
    <div style={{ display: "flex" }}>
      <div
        style={{
          userSelect: "none",
          WebkitUserSelect: "none",
          flexShrink: 0,
          width: 50,
          height: 50,
          marginRight: "10px",
          background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain`,
        }}
        draggable="false"
      />
      <div>
        {props?.value?.product_title}
        <br />
        <i style={{ fontWeight: "light", color: "#665", fontSize: "1rem" }}>
          {props?.value?.asin} ({props?.value?.sku})
          <div>{props?.original?.promotion_report_product?.promotion_report?.promotion_id}</div>
        </i>
      </div>
    </div>
  );
};

const col = (header, type, key, options) => ({
  header,
  type,
  key,
  options,
});

const makeColumn = (data, marketPlace, mobileStyle, date, dateFormatter) => (header, key, type, options) => {
  const { hideFilter } = options ?? {};
  const metricSpec = getMetricSpec(key);

  const defaultAccessor = (p) => jmespath.search(p, key);
  const accessor = options?.accessor ?? defaultAccessor;
  const defaultFormat = ({ value }) => value;
  const fmt =
    options?.formatter ??
    {
      currency: ({ value }) =>
        value == null ? "" : (
          formatCurrency(value, {
            marketPlace: marketPlace.marketPlace,
            decimalPlaces: options?.decimalPlaces ?? 0,
          })
        ),
      number: ({ value }) => (value == null ? "" : formatNumber(value, { decimalPlaces: 1 })),
      percent: ({ value }) =>
        value == null ? "" : (
          formatPercent(value, {
            decimalPlaces: 0,
          })
        ),
      date: ({ value }) => (value == null ? "" : dateFormatter.format(new Date(value))),
      category: ({ original }) => (
        <CategoryCell original={original} buildLink={(categoryId) => `/user/merchandising/category/${categoryId}`} />
      ),
      product: ProductCell,
      percentDiff: ({ value }) => (
        <span
          style={{
            color: value > 0 ? trellisPalette[12] : trellisPalette[17],
          }}
        >
          {value == null ?
            ""
          : formatPercent(value, {
              decimalPlaces: 0,
              addPlus: true,
            })
          }
        </span>
      ),
    }?.[type] ??
    defaultFormat;
  const parser =
    {
      currency: parseFormattedCurrency,
      number: parseFormattedNumber,
      percent: parseFormattedPercentage,
    }?.[type] ?? defaultFormat;

  // Handle tooltips to support metric selection menu descriptions.
  // The makeColumn factory runs in module scope, so it can't call t(). Pass the
  // tooltip selector through; consumers (useColumnSelector) resolve at render time.
  const toolTip = options?.toolTip ?? (($) => $.metrics.byId[key].toolTip);

  // Handle percent filter formatting
  if (type === "percent") {
    data = data.map((row) => {
      Object.keys(row).forEach((propertyKey) => {
        if (propertyKey === key) {
          row["formatted_" + propertyKey] = row[propertyKey] * 100;
        }
      });

      return row;
    });
  }

  return {
    Header: header,
    id: key,
    accessor,
    height: 30,
    grouping: metricSpec.grouping,
    toolTip,
    isStatic: options.isStatic,
    format: type,
    className: options?.className ?? "text-end",
    headerClassName: options?.className ?? "text-end",
    Cell: (props) => {
      if (options?.showDot ?? true) {
        const compareKey = options?.compareKey;
        if (compareKey) {
          const compareValue = jmespath.search(props.original, compareKey);
          return (
            <div>
              {fmt({ ...props, date })}
              <br />
              <span
                style={{
                  color: "#666",
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                }}
              >
                {compareValue === null ?
                  ""
                : <>
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "normal",
                      }}
                    >
                      {options.compareType}{" "}
                    </span>
                    {fmt({
                      ...props,
                      value: compareValue,
                      date,
                    })}
                  </>
                }
              </span>
            </div>
          );
        }
      }

      return fmt({ ...props, date });
    },

    style: { height: "60px" },
    filterMethod: (filter, row) => {
      const formattedPercentAccessor = (p) => p[`formatted_${key}`];
      const value = type === "percent" ? formattedPercentAccessor(row._original) : accessor(row._original);
      return filterRanges(filter, value, parser, marketPlace.marketPlace);
    },
    Filter: ({ filter, onChange }) => {
      if (hideFilter) {
        return null;
      }

      return generateFilterMarkup(filter, onChange, key, "range", type, {
        ranges: generateRanges(
          data ?? [],
          5,
          type === "percent" ? `formatted_${key}` : key,
          "number",
          marketPlace.marketPlace,
          true,
        ),
        marketPlace: marketPlace.marketPlace,
      });
    },
  };
};

const PROMOTION_TYPE_TO_KEY = {
  BEST_DEAL: "Best Deal",
  LIGHTNING_DEAL: "Lightning Deal",
  PRICE_DISCOUNT: "Price Discount",
};

const Table = ({ stats, marketPlace, mobileStyle, columnSpecs, date, dateFormatter, showPromotionFilters = false }) => {
  const { t } = useTranslation(["merchandising"]);
  const { PROMOTION_STATUS_FILTER, PROMOTION_TYPE_FILTER } = usePromotionReportFilters();
  const [searchFilter, setSearchFilter] = useState("");
  const [clearFilters, setClearFilters] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const TABLE_ID = "promotionsReportTable";
  const tableRef = useRef();

  const { customFilters, setCustomFilters, applyCustomFilters } = useCustomTableFilters(TABLE_ID);

  const data = useMemo(() => {
    return applyCustomFilters(
      stats
        ?.map((p) => {
          const promoTypeKey = PROMOTION_TYPE_TO_KEY[p?.promotion_report_product?.promotion_report?.promotion_type];
          const promotionType =
            promoTypeKey ? t(($) => $.merchandising[promoTypeKey]) : t(($) => $.merchandising["Unknown"]);
          let status = p?.promotion_report_product?.promotion_report?.status;
          const start = p?.promotion_report_product?.promotion_report?.start_datetime;
          const end = p?.promotion_report_product?.promotion_report?.end_datetime;
          if (status === "APPROVED") {
            if (moment(start).isBefore(moment()) && moment(end).isAfter(moment())) {
              status = t(($) => $.merchandising["In Progress"]);
            } else if (moment(end).isBefore(moment())) {
              status = t(($) => $.merchandising["Completed"]);
            } else {
              status = t(($) => $.merchandising["Approved"]);
            }
          } else {
            status = status === "CANCELED" ? t(($) => $.merchandising["Canceled"]) : status?.toLowerCase;
          }
          return {
            ...p,
            status,
            promotionType,
          };
        })
        ?.filter((p) => {
          if (searchFilter) {
            const searchable = `${p?.category_name} ${p?.product?.product_title} ${p?.product?.asin} ${p?.product?.sku}`;
            return searchable.toLowerCase()?.indexOf(searchFilter.toLowerCase()) > -1;
          }
          return true;
        }) ?? [],
    );

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, searchFilter, customFilters]);

  const { savedTableSettings: savedTablePageSize, updateTableSettings: setSavedTablePageSize } = useTableSettings(
    TABLE_ID,
    "tablePageSize",
    5,
  );

  const { savedTableSettings: savedTableSortMethod, updateTableSettings: setSavedTableSortMethod } = useTableSettings(
    TABLE_ID,
    "tableSorting",
    [],
  );

  const { savedTableSettings: savedColumnOrder, updateTableSettings: setSavedColumnOrder } = useTableSettings(
    TABLE_ID,
    "tableColOrder",
    [],
  );

  const createColumn = makeColumn(data, marketPlace, mobileStyle, date, dateFormatter);
  const [columns, columnSelectorProps] = useColumnSelector(() => {
    const specifiedColumns = columnSpecs.map((c) => createColumn(c.header, c.key, c.type, c.options));

    return sortTableColumns(specifiedColumns, savedColumnOrder);
  }, [columnSpecs, stats, mobileStyle]);
  const columnOrder = columns.map((col) => col.id);
  return (
    <ReactTable
      ref={tableRef}
      showPaginationBottom
      showPaginationTop
      PaginationComponent={ReactTablePagination}
      columns={columns}
      data={data}
      defaultPageSize={savedTablePageSize || 10}
      defaultSorted={savedTableSortMethod || []}
      className={`-highlight ${TABLE_ID}`}
      filterable={showFilters}
      state={columnOrder}
      onSortedChange={(newSorted) => {
        setSavedTableSortMethod(newSorted);
      }}
      onPageSizeChange={(pageSize) => {
        setSavedTablePageSize(pageSize);
      }}
      getTheadFilterProps={(props) => {
        if (clearFilters) {
          props.filtered.forEach((filter) => {
            filter.value = "all";
          });
          setClearFilters(false);
        }
        return { style: { display: "flex" } };
      }}
      getPaginationProps={() => ({
        setShowFilters: setShowFilters,
        updateSearch: setSearchFilter,
        clearFilters: () => {
          setSearchFilter("");
          setCustomFilters([]);
          setClearFilters(true);
        },
        hideFilters: false,
        showFilters: showFilters,
        clearSearchTermFilters: () => setSearchFilter(""),
        searchTerm: searchFilter,
        table: "plan-products",
        style: { marginBottom: "2rem" },
        carouselLayout: false,
        disableSearchFocus: true,
        columnSelectorProps: columnSelectorProps,
        filterWidget: true,
        hideFilterToggleIcon: true,
        savedColumnOrder: savedColumnOrder,
        setSavedColumnOrder: setSavedColumnOrder,
        entityType: "promotion",
        children: (
          <div className="d-flex justify-content-start w-100 h-100 align-items-center px-2">
            <RT6FilterWidget
              customFilters={customFilters}
              setCustomFilters={setCustomFilters}
              tableRef={tableRef}
              additionalFilterOptions={showPromotionFilters ? [PROMOTION_TYPE_FILTER, PROMOTION_STATUS_FILTER] : ""}
              excludedFilterColumns={showPromotionFilters ? ["product", "promotionType", "status"] : ["category_name"]}
              tableId={TABLE_ID}
            />
          </div>
        ),
      })}
    />
  );
};

const PromotionsReport = () => {
  const { t } = useTranslation(["merchandising"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { date, start, end, group } = useDates();
  const longDate = useDateFormatter("long");
  const shortDate = useDateFormatter("short");
  const [tab, setTab] = useState("inprogress");
  const {
    data: promoReportData,
    isLoading,
    isError,
  } = useFetch(
    ["promosReport", start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")],
    `/merchandise/promos_report/`,
    {
      group_by: group,
      market_place: marketPlace.marketPlace,
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
    },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const breadCrumbItems = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.merchandising["Deals Dashboard"]),
      href: "/user/merchandising/promotions",
      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      unsorted: true,
      dropdownSubtitle: t(($) => $.merchandising["Dashboards"]),
    },
  ];

  const currencyFormatter = (v) =>
    formatCurrency(v, {
      marketPlace: marketPlace.marketPlace,
      compact: true,
    });

  const [metrics, inprogressMetrics, upcomingMetrics, catColumns, promoColumns, upcomingColumns] = useMemo(() => {
    if (promoReportData) {
      const total = promoReportData.total;
      const inprogress = promoReportData.summary.inprogress;
      const upcoming = promoReportData.summary.upcoming;

      return [
        [
          {
            id: "promo_sales",
            name: t(($) => $.merchandising["Deals Sales"]),
            color: metricColors.sales[2],
            accessor: (d) => d.promo_sales,
            actual: total.promo_sales,
            formatter: currencyFormatter,
            format: "currency",
            compact: true,
            axis: 1,
          },
          {
            id: "promo_units",
            name: t(($) => $.merchandising["Deals Units"]),
            color: metricColors.units[1],
            accessor: (d) => d.promo_units,
            actual: total.promo_units,
            formatter: (v) => formatNumber(v, { notation: "compact", decimalPlaces: 0 }),
            format: "number",
            decimalPlaces: 2,
            compact: true,
            axis: 0,
            chartType: "column",
          },
          {
            id: "promo_cost",
            name: t(($) => $.merchandising["Total Discounts"]),
            color: metricColors.cost[2],
            accessor: (d) => d.promo_cost,
            actual: total.promo_cost,
            target: null,
            formatter: currencyFormatter,
            format: "currency",
            compact: true,
            axis: 1,
          },
          {
            id: "promo_glance_views",
            name: t(($) => $.merchandising["Glance Views"]),
            color: trellisPalette[1],
            accessor: (d) => d.glance_views,
            actual: total.glance_views,
            target: null,
            formatter: (v) => formatNumber(v, { notation: "compact", decimalPlaces: 0 }),
            format: "number",
            decimalPlaces: 2,
            compact: true,
            axis: 3,
          },
          {
            id: "promo_conv_rate",
            name: t(($) => $.merchandising["Deals Conv. Rate"]),
            color: trellisPalette[2],
            accessor: (d) => d.conv,
            actual: total.conv,
            target: null,
            formatter: (v) => formatPercent(v),
            format: "percent",
            decimalPlaces: 2,
            axis: 4,
          },
        ],
        [
          {
            id: "promo_sales",
            name: t(($) => $.merchandising["Deals Sales"]),
            color: metricColors.sales[2],
            accessor: (d) => d.promo_sales,
            actual: inprogress.product_revenue,
            formatter: currencyFormatter,
            format: "currency",
            compact: true,
            axis: 1,
          },
          {
            id: "promo_units",
            name: t(($) => $.merchandising["Deals Units"]),
            color: metricColors.units[1],
            accessor: (d) => d.promo_units,
            actual: inprogress.product_units_sold,
            formatter: (v) => formatNumber(v, { notation: "compact", decimalPlaces: 0 }),
            format: "number",
            decimalPlaces: 2,
            compact: true,
            axis: 0,
            chartType: "column",
          },
        ],
        [
          {
            id: "sales_est",
            name: t(($) => $.merchandising["Sales Est"]),
            accessor: () => upcoming.sales_est,
            actual: upcoming.sales_est,
            formatter: currencyFormatter,
            format: "currency",
          },
          {
            id: "units_est",
            name: t(($) => $.merchandising["Units Est"]),
            accessor: (d) => d.promo_units,
            actual: upcoming.units_est,
            formatter: (v) => formatNumber(v, { notation: "compact", decimalPlaces: 0 }),
            format: "number",
            decimalPlaces: 2,
            compact: true,
          },
        ],
        [
          col(
            t(($) => $.merchandising["Category"]),
            "category",
            "category_name",
            {
              className: "text-start font-weight-bold",
              width: 300,
              mobileWidth: 200,
              showDot: false,
              fixedLeft: true,
              hideFilter: true,
            },
          ),
          col(
            t(($) => $.merchandising["Promo Cost"]),
            "currency",
            "promo_cost",
            {
              toolTip: t(($) => $.merchandising["Total cost from promotion"]),
            },
          ),
        ],
        [
          col(
            t(($) => $.merchandising["Product"]),
            "product",
            "product",
            {
              className: "text-start font-weight-bold",
              width: 300,
              mobileWidth: 200,
              showDot: false,
              fixedLeft: true,
              hideFilter: true,
              isStatic: true,
              toolTip: t(($) => $.merchandising["The promoted product"]),
            },
          ),
          col(
            t(($) => $.merchandising["Status"]),
            "text",
            "status",
            {
              hideFilter: true,
              toolTip: t(($) => $.merchandising["The current status of the promotion"]),
            },
          ),
          col(
            t(($) => $.merchandising["Type"]),
            "text",
            "promotionType",
            {
              hideFilter: true,
              toolTip: t(($) => $.merchandising["The type of promotion"]),
            },
          ),
          col(
            t(($) => $.merchandising["Start"]),
            "date",
            "promotion_report_product.promotion_report.start_datetime",
            {
              hideFilter: true,
              toolTip: t(($) => $.merchandising["The start date of the promotion"]),
            },
          ),
          col(
            t(($) => $.merchandising["End"]),
            "date",
            "promotion_report_product.promotion_report.end_datetime",
            {
              hideFilter: true,
              toolTip: t(($) => $.merchandising["The end date of the promotion"]),
            },
          ),
          col(
            t(($) => $.merchandising["Deal Price"]),
            "currency",
            "deal_price",
            {
              decimalPlaces: 2,
              toolTip: t(($) => $.merchandising["The reduced price for the duration of the promotion"]),
            },
          ),

          col(
            t(($) => $.merchandising["Total Discounts"]),
            "currency",
            "cost",
            {
              toolTip: t(($) => $.merchandising["Total cost of the promotion"]),
            },
          ),
          col(
            t(($) => $.merchandising["Glance Views"]),
            "number",
            "product_glance_views",
            {
              toolTip: t(($) => $.merchandising["Total number of glance views from promotion"]),
            },
          ),
          col(
            t(($) => $.merchandising["Conversion Rate"]),
            "percent",
            "conversion",
            {
              toolTip: t(($) => $.merchandising["The percentage of clicks that result in a sale"]),
            },
          ),
        ],
        [
          col(
            t(($) => $.merchandising["Product"]),
            "product",
            "product",
            {
              className: "text-start font-weight-bold",
              width: 300,
              mobileWidth: 200,
              showDot: false,
              fixedLeft: true,
              isStatic: true,
              toolTip: t(($) => $.merchandising["The promoted product"]),
            },
          ),
          col(
            t(($) => $.merchandising["Status"]),
            "text",
            "status",
            {
              toolTip: t(($) => $.merchandising["The current status of the promotion"]),
            },
          ),
          col(
            t(($) => $.merchandising["Type"]),
            "text",
            "promotionType",
            {
              toolTip: t(($) => $.merchandising["The type of promotion"]),
            },
          ),
          col(
            t(($) => $.merchandising["Start"]),
            "date",
            "promotion_report_product.promotion_report.start_datetime",
            {
              toolTip: t(($) => $.merchandising["The start date of the promotion"]),
            },
          ),
          col(
            t(($) => $.merchandising["End"]),
            "date",
            "promotion_report_product.promotion_report.end_datetime",
            {
              toolTip: t(($) => $.merchandising["The end date of the promotion"]),
            },
          ),
          col(
            t(($) => $.merchandising["Deal Price"]),
            "currency",
            "deal_price",
            {
              decimalPlaces: 2,
              toolTip: t(($) => $.merchandising["The reduced price for the duration of the promotion"]),
            },
          ),

          col(
            t(($) => $.merchandising["Units Est"]),
            "number",
            "units_est",
            {
              toolTip: t(($) => $.merchandising["Estimated units from promotion"]),
            },
          ),
          col(
            t(($) => $.merchandising["Cost Est"]),
            "currency",
            "cogs_est",
            {
              toolTip: t(($) => $.merchandising["Estimated cost of running the promotion"]),
            },
          ),
        ],
      ];
    }
    return [[], [], [], [], [], []];

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [promoReportData]);

  return (
    <Grid fluid className="py-3" style={{ minHeight: "calc(100vh - 150px)" }}>
      <Row style={{ marginTop: "-10px", marginBottom: "1rem" }}>
        <Col xs={12}>
          <BreadcrumbDates items={breadCrumbItems} hideCompare />
        </Col>
        {promoReportData && promoReportData.has_ext_data && (
          <Col
            xs={12}
            className="text-center fs-standard"
            style={{
              padding: "2rem",
              backgroundColor: "#eee",
              position: "relative",
            }}
          >
            <span>
              <a href="https://chrome.google.com/webstore/category/extensions">
                <Trans
                  i18nKey={($) => $.merchandising["Click here to download the <ext>Trellis Chrome Extension</ext>"]}
                  components={{ ext: <b /> }}
                />
              </a>
            </span>
          </Col>
        )}
      </Row>
      <Row>
        <Col xs={12}>
          {isLoading && <Loading />}
          {isError && <p>{t(($) => $.merchandising["Error Loading Dashboard"])}</p>}
          {promoReportData && (
            <MetricsCard
              title={t(($) => $.merchandising["Deals"])}
              totals={{
                data: promoReportData.total,
                loading: isLoading,
              }}
              timeline={{
                data: promoReportData.dates ?? [],
                loading: isLoading,
              }}
              metrics={metrics}
              initialSelectedMetric={["promo_sales", "promo_units", "promo_cost"]}
              byline={
                <>
                  {longDate.format(start.toDate())}
                  {" - "}
                  {longDate.format(end.toDate())}
                  {isLoading && <i className="fa fa-spinner fa-spin"></i>}
                </>
              }
            >
              <Row>
                <Col xs={12} className="mt-5 ph-5 mb-5 border-bottom fs-standard">
                  <Nav
                    variant="underline"
                    onSelect={(k) => {
                      return setTab(k);
                    }}
                    activeKey={tab}
                  >
                    <Nav.Item>
                      <Nav.Link eventKey="inprogress">{t(($) => $.merchandising["In Progress"])}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="upcoming">{t(($) => $.merchandising["Upcoming"])}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="completed">{t(($) => $.merchandising["Past Promotions"])}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="categories">{t(($) => $.merchandising["Categories"])}</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Col>
              </Row>
              {tab === "upcoming" && (
                <Row>
                  <Col xs={6}>
                    <Row>
                      {upcomingMetrics.map((m, i) => (
                        <Col xs={3} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                          <div className="">
                            <div>
                              <DetailedMetric center metric={m} data={promoReportData?.total} showTarget={!!m.target} />
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Col>
                  <Col xs={12}>
                    <Table
                      stats={promoReportData?.upcoming_promos}
                      marketPlace={marketPlace}
                      columnSpecs={upcomingColumns}
                      date={date}
                      dateFormatter={shortDate}
                      showPromotionFilters
                    />
                  </Col>
                </Row>
              )}
              {tab === "inprogress" && (
                <Row>
                  <Col xs={6}>
                    <Row>
                      {inprogressMetrics.map((m, i) => (
                        <Col xs={3} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                          <div className="">
                            <div>
                              <DetailedMetric center metric={m} data={promoReportData?.total} showTarget={!!m.target} />
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Col>
                  <Col xs={12}>
                    <Table
                      stats={promoReportData?.inprogress_promos}
                      marketPlace={marketPlace}
                      columnSpecs={promoColumns}
                      date={date}
                      dateFormatter={shortDate}
                      showPromotionFilters
                    />
                  </Col>
                </Row>
              )}
              {tab === "completed" && (
                <Row>
                  <Col xs={12}>
                    <Table
                      stats={promoReportData?.promos}
                      marketPlace={marketPlace}
                      columnSpecs={promoColumns}
                      date={date}
                      dateFormatter={shortDate}
                      showPromotionFilters
                    />
                  </Col>
                </Row>
              )}
              {tab === "categories" && (
                <Row>
                  <Col xs={12}>
                    <Table
                      stats={promoReportData?.categories}
                      marketPlace={marketPlace}
                      columnSpecs={catColumns}
                      date={date}
                      dateFormatter={shortDate}
                    />
                  </Col>
                </Row>
              )}
              <Row>
                <Col xs={12} className="text-end">
                  <DownloadReport
                    url_path={"promos_report/"}
                    filters={{
                      market_place: marketPlace.marketPlace,
                      start: start.format("YYYY-MM-DD"),
                      end: end.format("YYYY-MM-DD"),
                    }}
                    endpoint="/merchandise/"
                  />
                </Col>
              </Row>
            </MetricsCard>
          )}
        </Col>
      </Row>
    </Grid>
  );
};

const ReportsDashboard = () => {
  const user = useAppSelector((state) => state.user);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);
  const channel = useAppSelector((state) => state.channel);
  const hasPromotionsModule = useModuleEnabled("PROMOTIONS");

  const PROMO_SUBSCRIPTION =
    ((user.promotions_tier && !["unknown"].includes(user.promotions_tier)) ||
      ((hasPromotionsModule || checkTierAccess(user.tier, "promotions", amz_onboard.org_status)) &&
        (amz_onboard.org_status === "active" ||
          amz_onboard.org_status === "trial_active" ||
          amz_onboard.org_status === "demo_org" ||
          channel.currentChannel === "walmart"))) &&
    !["MANUALLY_DISABLED"].includes(user.promotions_tier);

  if (!PROMO_SUBSCRIPTION) {
    return <PaymentDiscovery discoveryType={"advertising"} dashboardType="advertising" />;
  }
  return (
    <DatesProvider>
      <PromotionsReport />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/merchandising/promotions")({
  component: ReportsDashboard,
});
